import { useState, useMemo, useEffect, useCallback } from 'react';
import BillingUsageService from '@Helpers/URLHelpers/BillingService/BillingUsageService/BillingUsageService';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    CircleCheck,
    Database,
    BookOpen,
    Users,
    Settings2,
    HelpCircle,
    LogOut,
    CreditCard,
    ChevronRight,
    Menu as MenuIcon,
    X as CloseIcon,
    Zap,
    RefreshCw,
    AlertTriangle,
} from 'lucide-react';
import { ReactComponent as KaizIQText } from '@Assets/kaiziqText.svg';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxSubscription } from '@Hooks/reduxSubscriptionHook/useReduxSubscription';
import { useReduxBillingUsage } from '@Hooks/reduxBillingUsageHook/useReduxBillingUsage';
import { useMediaQuery } from '@mui/material';
import './SidePanelAdmin.scss';

const STATUS_META = {
    ACTIVE:    { label: 'Active',     color: '#16a34a', bg: 'rgba(22,163,74,0.15)' },
    TRIALING:  { label: 'Trial',      color: '#d97706', bg: 'rgba(217,119,6,0.15)' },
    HALTED:    { label: 'Payment Failed', color: '#dc2626', bg: 'rgba(220,38,38,0.15)' },
    CANCELLED: { label: 'Cancelled',  color: '#64748b', bg: 'rgba(100,116,139,0.15)' },
    CREATED:   { label: 'Activating', color: '#2563eb', bg: 'rgba(37,99,235,0.15)' },
};

const SUBSCRIBED_STATUSES = new Set(['ACTIVE', 'TRIALING', 'HALTED', 'CREATED']);

export default function SidePanelAdmin() {

    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [mobileOpen, setMobileOpen] = useState(false);

    const { activeUser, activeUserId, userType } = useReduxActiveUser();
    const { currentOrg, currentUserOrgPermissions } = useReduxOrg();
    const { subscription, isHaltedButWithinGracePeriod } = useReduxSubscription();

    // Billing is visible to org admins in addition to the owner — everyone else is a regular
    // member and shouldn't see the "Manage Billing" shortcut.
    const isOrgOwner = !currentOrg || currentOrg.ownerUserId === activeUserId;
    const canManageBilling = isOrgOwner || !!currentUserOrgPermissions?.allowAdmin;

    const className = 'sidePanelAdmin';

    const userName = activeUser?.fullName || activeUser?.userName || 'User';
    const userEmail = activeUser?.email || '';

    const initials = useMemo(() => {
        const parts = (userName || 'U').trim().split(/\s+/);
        const first = parts[0]?.[0] || '';
        const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : '';
        return (first + (last || '')).toUpperCase();
    }, [userName]);

    const { usage, setCurrentBillingUsage, setCurrentLastMonthBillingUsage } = useReduxBillingUsage();
    const [usageRefreshing, setUsageRefreshing] = useState(false);
    const [lastRefreshed, setLastRefreshed] = useState(null);
    const [, setDisplayTick] = useState(0);

    const isSubscribed = SUBSCRIBED_STATUSES.has(subscription?.status);
    const isCancelled = subscription?.status === 'CANCELLED';
    const currentPeriodEndDate = subscription?.currentPeriodEnd ? new Date(subscription.currentPeriodEnd) : null;
    const hasCancelledAccessWindow =
        isCancelled &&
        !!currentPeriodEndDate &&
        !Number.isNaN(currentPeriodEndDate.getTime()) &&
        currentPeriodEndDate > new Date();
    const hasWorkspaceAccess = isSubscribed || hasCancelledAccessWindow;

    const plan = subscription?.plan;
    const statusMeta = STATUS_META[subscription?.status] || {
        label: subscription?.status || '—',
        color: '#64748b',
        bg: 'rgba(100,116,139,0.15)',
    };

    const ticketsUsed = usage?.ticketCount ?? 0;
    const resolvedTicketLimit = usage?.planLimitSnapshot ?? plan?.ticketLimitMonthly ?? 500;
    const ticketPct   = resolvedTicketLimit === -1 ? 0 : Math.min((ticketsUsed / resolvedTicketLimit) * 100, 100);
    const ticketFillClass = ticketPct >= 100 ? '--full' : ticketPct >= 80 ? '--warn' : '';

    const agentsUsed  = subscription?.agentSeatCount ?? 1;
    const agentLimit  = plan?.maxAgents ?? -1;
    const agentPct    = agentLimit === -1 ? 0 : Math.min((agentsUsed / agentLimit) * 100, 100);
    const agentFillClass = agentPct >= 100 ? '--full' : agentPct >= 80 ? '--warn' : '';

    const fetchUsage = useCallback(async () => {
        const orgId = subscription?.orgId;
        if (!orgId) return;
        setUsageRefreshing(true);
        try {
            const billingUsageService = new BillingUsageService();
            const [currentRes, historyRes] = await Promise.all([
                billingUsageService.getCurrentUsageByOrgId(orgId),
                billingUsageService.getHistoryByOrgId(orgId),
            ]);

            if (currentRes?.body) {
                const parsed = JSON.parse(currentRes.body);
                // API returns the usage object directly; handle both {data:{...}} and {...} shapes
                const usageData = parsed?.data ?? parsed;
                if (usageData) {
                    setCurrentBillingUsage(usageData);
                    setLastRefreshed(new Date());

                    if (historyRes?.body) {
                        const historyParsed = JSON.parse(historyRes.body);
                        const historyList = Array.isArray(historyParsed?.data)
                            ? historyParsed.data
                            : Array.isArray(historyParsed)
                                ? historyParsed
                                : [];

                        const [yr, mo] = usageData.billingMonth.split('-').map(Number);
                        const lastMo = mo === 1 ? 12 : mo - 1;
                        const lastYr = mo === 1 ? yr - 1 : yr;
                        const lastMonthStr = `${lastYr}-${String(lastMo).padStart(2, '0')}`;

                        const lastMonthEntry = historyList.find(u => u.billingMonth === lastMonthStr) ?? null;
                        setCurrentLastMonthBillingUsage(lastMonthEntry);
                    }
                }
            }
        } finally {
            setUsageRefreshing(false);
        }
    }, [subscription?.orgId, setCurrentBillingUsage, setCurrentLastMonthBillingUsage]);

    useEffect(() => {
        fetchUsage();
    }, [fetchUsage]);

    useEffect(() => {
        window.addEventListener('ticket:created', fetchUsage);
        return () => window.removeEventListener('ticket:created', fetchUsage);
    }, [fetchUsage]);

    // Auto-refresh usage every 5 minutes
    useEffect(() => {
        const id = setInterval(fetchUsage, 5 * 60 * 1000);
        return () => clearInterval(id);
    }, [fetchUsage]);

    // Tick every 30s so "X mins ago" label stays current
    useEffect(() => {
        const id = setInterval(() => setDisplayTick(t => t + 1), 30_000);
        return () => clearInterval(id);
    }, []);

    const lastRefreshedLabel = useCallback(() => {
        if (!lastRefreshed) return null;
        const diffMins = Math.floor((Date.now() - lastRefreshed.getTime()) / 60_000);
        if (diffMins < 1) return 'just now';
        if (diffMins === 1) return '1 min ago';
        return `${diffMins} mins ago`;
    }, [lastRefreshed]);

    const closeMobile = () => setMobileOpen(false);

    const navItem = ({ isActive }) =>
        `${className}__nav__item${isActive ? ' is-active' : ''}`;

    const sidebar = (
        <aside className={className + '__sidebar'}>

            {/* ── Logo ── */}
            <div className={className + '__logo'} onClick={() => { closeMobile(); navigate('/dashboard'); }}>
                <KaizIQText className={className + '__logo__svg'} />
            </div>

            {/* ── Navigation — shown while workspace access is active ── */}
            {hasWorkspaceAccess && (
                <nav className={className + '__nav'}>

                    <div className={className + '__nav__group'}>
                        <span className={className + '__nav__label'}>Workspace</span>

                        <NavLink to="/dashboard" className={navItem} onClick={closeMobile}>
                            <LayoutDashboard size={16} />
                            <span>Dashboard</span>
                        </NavLink>
                        <NavLink to="/forms" className={navItem} onClick={closeMobile}>
                            <CircleCheck size={16} />
                            <span>Forms</span>
                        </NavLink>
                        <NavLink to="/registries" className={navItem} onClick={closeMobile}>
                            <Database size={16} />
                            <span>Registries</span>
                        </NavLink>

                        <NavLink to="/docs" className={navItem} onClick={closeMobile}>
                            <BookOpen size={16} />
                            <span>Docs</span>
                        </NavLink>
                    </div>

                    <div className={className + '__nav__group'}>
                        <span className={className + '__nav__label'}>Admin</span>

                        <NavLink
                            to="/userManagement/teams-users"
                            className={navItem}
                            onClick={closeMobile}
                        >
                            <Users size={16} />
                            <span>Teams & Users</span>
                        </NavLink>

                        <NavLink
                            to="/userManagement/settings"
                            end
                            className={navItem}
                            onClick={closeMobile}
                        >
                            <Settings2 size={16} />
                            <span>Settings</span>
                        </NavLink>
                    </div>

                </nav>
            )}

            {/* ── No-plan prompt ── */}
            {!hasWorkspaceAccess && (
                <div className={className + '__no-plan'}>
                    <Zap size={22} className={className + '__no-plan__icon'} />
                    <p className={className + '__no-plan__text'}>
                        Choose a plan to unlock your workspace
                    </p>
                    <button
                        className={className + '__no-plan__cta'}
                        onClick={() => { closeMobile(); navigate('/subscribe'); }}
                    >
                        View Plans
                        <ChevronRight size={14} />
                    </button>
                </div>
            )}

            {/* ── Current plan widget ── */}
            {hasWorkspaceAccess && plan && (
                <div className={className + '__plan'}>
                    <div className={className + '__plan__row'}>
                        <div className={className + '__plan__icon'}>
                            <Zap size={13} />
                        </div>
                        <span className={className + '__plan__name'}>{plan.name}</span>
                        <span
                            className={className + '__plan__badge'}
                            style={{ color: statusMeta.color, background: statusMeta.bg }}
                        >
                            {statusMeta.label}
                        </span>
                    </div>

                    {isCancelled && (
                        <div className={className + '__plan__cancelNotice'}>
                            <AlertTriangle size={13} />
                            <div>
                                <div className={className + '__plan__cancelNotice__title'}>
                                    Subscription cancelled
                                </div>
                                <div className={className + '__plan__cancelNotice__date'}>
                                    Access ends&nbsp;
                                    <strong>
                                        {subscription?.currentPeriodEnd
                                            ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                            : '—'}
                                    </strong>
                                </div>
                            </div>
                        </div>
                    )}

                    {isHaltedButWithinGracePeriod && (
                        <div className={className + '__plan__cancelNotice'}>
                            <AlertTriangle size={13} />
                            <div>
                                <div className={className + '__plan__cancelNotice__title'}>
                                    Payment failed
                                </div>
                                <div className={className + '__plan__cancelNotice__date'}>
                                    Update by&nbsp;
                                    <strong>
                                        {subscription?.gracePeriodEnd
                                            ? new Date(subscription.gracePeriodEnd).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                            : '—'}
                                    </strong>
                                </div>
                            </div>
                        </div>
                    )}

                    {(!isCancelled || hasCancelledAccessWindow) && (
                        <>
                            <div className={className + '__plan__usage'}>
                                {/* Tickets */}
                                <div className={className + '__plan__usage-row'}>
                                    <div className={className + '__plan__usage-label'}>
                                        <span>Tickets / mo</span>
                                        <span>
                                            {ticketsUsed}
                                            {resolvedTicketLimit !== -1 ? ` / ${resolvedTicketLimit}` : ' / ∞'}
                                        </span>
                                    </div>
                                    <div className={className + '__plan__usage-bar'}>
                                        <div
                                            className={`${className}__plan__usage-bar__fill${ticketFillClass ? ' ' + className + '__plan__usage-bar__fill' + ticketFillClass : ''}`}
                                            style={{ width: resolvedTicketLimit === -1 ? '0%' : `${ticketPct}%` }}
                                        />
                                    </div>
                                </div>

                                {/* Agents */}
                                <div className={className + '__plan__usage-row'}>
                                    <div className={className + '__plan__usage-label'}>
                                        <span>Agents</span>
                                        <span>
                                            {agentsUsed}
                                            {agentLimit !== -1 ? ` / ${agentLimit}` : ''}
                                        </span>
                                    </div>
                                    {agentLimit !== -1 && (
                                        <div className={className + '__plan__usage-bar'}>
                                            <div
                                                className={`${className}__plan__usage-bar__fill${agentFillClass ? ' ' + className + '__plan__usage-bar__fill' + agentFillClass : ''}`}
                                                style={{ width: `${agentPct}%` }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <button
                                className={`${className}__plan__refreshBtn${usageRefreshing ? ' is-loading' : ''}`}
                                onClick={fetchUsage}
                                disabled={usageRefreshing}
                                title="Refresh usage"
                            >
                                <RefreshCw size={10} />
                                {lastRefreshed
                                    ? `refreshed ${lastRefreshedLabel()}`
                                    : 'Refresh usage'
                                }
                            </button>
                        </>
                    )}

                    {canManageBilling && (
                        <button
                            className={className + '__plan__link'}
                            onClick={() => { closeMobile(); navigate('/userManagement/settings?tab=5'); }}
                        >
                            <CreditCard size={12} />
                            Manage Billing
                            <ChevronRight size={12} />
                        </button>
                    )}
                </div>
            )}

            <div className={className + '__spacer'} />

            {/* ── Help & Support ── */}
            <NavLink to="/support" className={className + '__help'} onClick={closeMobile}>
                <HelpCircle size={15} />
                <span>Help & Support</span>
            </NavLink>

            {/* ── User profile ── */}
            <div className={className + '__profile'}>
                <div className={className + '__profile__avatar'}>
                    {activeUser?.photo && activeUser.photo !== 'null' && activeUser.photo !== ''
                        ? <img src={activeUser.photo} alt={userName} />
                        : <span>{initials}</span>
                    }
                </div>

                <div className={className + '__profile__info'}>
                    <span className={className + '__profile__name'} title={userName}>{userName}</span>
                    <span className={className + '__profile__role'}>{userType || 'Admin'}</span>
                </div>

                <button
                    className={className + '__profile__logout'}
                    onClick={() => navigate('/logout')}
                    title="Logout"
                    aria-label="Logout"
                >
                    <LogOut size={14} />
                </button>
            </div>

        </aside>
    );

    if (isMobile) {
        return (
            <div className={className}>
                <button
                    className={className + '__toggle'}
                    onClick={() => setMobileOpen(prev => !prev)}
                    aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                >
                    {mobileOpen ? <CloseIcon size={20} /> : <MenuIcon size={20} />}
                </button>

                {mobileOpen && (
                    <>
                        <div className={className + '__overlay'} onClick={closeMobile} />
                        {sidebar}
                    </>
                )}
            </div>
        );
    }

    return <div className={className}>{sidebar}</div>;
}
